/**
 * A record's relationships, INLINE — the band that opens under a grid row.
 *
 * ── What it is ─────────────────────────────────────────────────────────────
 * The same derivation as `RecordRelations` (slots from the metamodel, counts
 * from `core.related_counts`), laid out for the inside of a list instead of the
 * top of a page: a GRID BAR over a grid, where the bar's view selector chooses
 * which relationship is open, and each row of it can open a band of its own.
 *
 * ── Why a chooser, and not a fixed child list ──────────────────────────────
 * A nested table assumes one canonical parent → child. A record here has N
 * registered relationships in BOTH directions, so "which child?" is a question
 * only the user can answer — hence a chooser, with counts, so the answer can be
 * given without opening anything to find out it is empty.
 *
 * ── Why it chains rather than nests ────────────────────────────────────────
 * Only the DEEPEST band shows its chooser; every ancestor collapses to the
 * chosen slot's name. So the chrome is constant no matter how deep you are,
 * which is the thing that makes depth survivable — the usual advice against
 * deep nesting is really advice against indentation accumulating inside one
 * scroll rhythm.
 *
 * Two limits are structural, not taste:
 *  · MAX_DEPTH — past it, rows stop offering a band and offer the record
 *    instead. An unbounded drill is one where every path is possible and none
 *    is designed.
 *  · `visited` — `works_at` → People → `works_at` → Organization is a legal
 *    infinite path on a graph. A row already on the path opens as a jump, not
 *    as another band.
 */
import { useMemo, useState, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import type { Row } from '@solum/sdk'
import { Button, CollectionWorkspace, DataGrid, EntityIcon, Form, GridColumnMenu, GridSearch, SavedViewSelector, Spinner, ViewSettingsMenu, type GridColumn, type GridGrouping, type GridRow } from '@solum/ui'
import { LayoutGrid, Pencil, Table2, Unlink } from 'lucide-react'
import { displayLabel, useRecordIdentities, useSchema } from '../data/queries'
import { useDefaultColumns } from '../list/defaultColumns'
import { extendTrail } from './trail'
import { openPalette } from '../app/paletteScope'
import { listSlots, relationshipWriteOffered } from './relations'
import { RECORD_COLUMN, useRelationCollection, type RelationFilterTerm, type RelationSortTerm, type RelationView } from './relationCollection'
import type { RelationSlot } from './relations'
import { singularize, type PeekRecipe } from '@solum/experience'
import { useEntityIdentities } from '../data/queries'
import { PeekRow } from './PeekRow'
import { relationshipAttributeFields, relationshipLifecycleFields, useEndRelationshipEdge, useRelatedCounts, useRelationRows, useUpdateRelationshipEdge, type RelationRow, type RelationRows } from './useRelations'
import { RelationshipTabs } from './RelationshipTabs'
import { openQuickCreate } from './quickCreate'
import { useViews } from '../experience/governed'
import s from './RelationBand.module.css'
import { useNestedViewChoice } from './useNestedViewChoice'
import { useListActionSeam } from '../list/listActionSeam'
import { RELATED_GROUP, useRelatedOrder } from './useRelatedOrder'
import { relationFooterContext } from './relationCollection'

/** Depth 0 is the band under a grid row; 3 bands deep is the floor of the drill. */
const MAX_DEPTH = 2

export interface RelationBandProps {
  entity: string
  id: string
  /** how to name this record in the breadcrumb and in the create header */
  label?: string
  depth?: number
  /** `entity:id` of every record already on this path — the cycle guard */
  visited?: string[]
  /** the trail of slot labels above this band, for the deep breadcrumb */
  trail?: string[]
  /**
   * Open a related record IN PLACE — the split beside the grid.
   *
   * Christopher, 2026-08-07, walking the cascade on mbn: "Open child records of
   * moonbase. See 2 contacts. Click on contact. Screen opens in normal form."
   * It did: the row was a `<Link>` to `/e/$entity/$id`, a whole-page navigation
   * away from the list he was reading. When the host list can show a detail
   * pane it passes this, and the click lands there instead. Absent (the record
   * PAGE's own bands, which have no grid beside them), the link stands.
   */
  onOpenRecord?: (entity: string, id: string, name?: string) => void
}

export function RelationBand({ entity, id, label, depth = 0, visited = [], trail = [], onOpenRecord }: RelationBandProps) {
  const schema = useSchema()
  const slots = useMemo(() => listSlots(schema.data, entity), [schema.data, entity])
  const { bySlot } = useRelatedCounts(entity, id, slots.length > 0)
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [presentation, setPresentation] = useState<'grid' | 'cards'>('grid')
  /* Selection is per grid INSTANCE and per tab (#2375 §4): selecting inside a
     nested grid never touches the parent's bar. Transient, so it may be scoped
     by the parent record; it is never persisted. */
  const [selection, setSelection] = useState<Set<string>>(new Set())
  /* The grid reflowed to cards: header and column ⋮ are inert (#2375). */
  const [headersHidden, setHeadersHidden] = useState(false)

  /**
   * The slot this band is showing.
   *
   * The default is the first slot that HAS something or whose count is still
   * unknown. A measured zero is the only answer quiet enough to skip: treating
   * an absent or refused count as zero would hide a relationship we have not read.
   */
  const slot: RelationSlot | undefined = useMemo(() => {
    if (pickedId) { const hit = slots.find((x) => x.id === pickedId); if (hit) return hit }
    return slots.find((x) => (bySlot.get(x.id)?.n ?? 0) > 0)
      ?? slots.find((x) => !bySlot.has(x.id))
      ?? slots[0]
  }, [pickedId, slots, bySlot])

  const targetEntity = slot?.otherEntity ?? entity
  const defaultShape = useDefaultColumns(targetEntity, 4)
  const savedViews = useViews(targetEntity)
  const viewRows = useMemo(() => (savedViews.data ?? []) as RelationView[], [savedViews.data])
  /* The relationship's saved view, SHARED across parent records (ADR F-84 §4):
     keyed by this parent entity TYPE and the slot, never by `id`. */
  const [viewId, setViewId] = useNestedViewChoice(entity, slot?.id)
  const activeView = viewRows.find((view) => String(view.id) === viewId) ?? viewRows[0]
  /* The rows are read HERE, not inside the grid, because the grid bar above the
     grid needs them too: the scoped palette lists the rows of the thing you
     invoked it from, and a palette that listed the child ENTITY instead would
     offer records this band is not showing. One read, both consumers. */
  const related = useRelationRows(slot ?? null, id)
  const rowItems = useMemo(
    () => related.rows.map(({ record }) => ({ id: String(record.id), label: displayLabel(record) })),
    [related.rows])
  const collection = useRelationCollection({
    related,
    slot,
    defaultColumns: defaultShape.columns,
    activeView,
    resetKey: `${slot?.id ?? ''}:${String(activeView?.id ?? '')}`,
    /* This grid's PATH (#2375 §9.4): every record above it, the row it hangs
       from, and the relationship — per tab, for the session. */
    sessionKey: `${[...visited, `${entity}:${id}`].join('/')}#${slot?.id ?? ''}`,
    labelOf: (key) => key === RECORD_COLUMN
      ? (slot?.otherLabel ?? 'Record')
      : key === '__validity' ? 'Validity'
        : String(slot?.attributes.find((attribute) => attribute.key === key)?.label
          ?? defaultShape.fields.get(key)?.label ?? key),
  })
  const { search, setSearch } = collection
  /* Governed BATCH acts on the far record (ADR F-84 §1). Selection is offered
     only where there is something to do with it. */
  const farActions = useListActionSeam(targetEntity)
  const identities = useEntityIdentities()
  const relatedOrder = useRelatedOrder(entity)
  /* Every hook sits ABOVE this return: while the schema loads there are no
     slots, and a hook below it would run only once they arrive (React #310). */

  if (slots.length === 0) {
    return <p className={s.none}>No relationships are registered for this record type yet.</p>
  }

  const here = `${entity}:${id}`
  const path = [...visited, here]
  // `--rel-N` is brand-emitted (#2019); the `--brand` fallback keeps a spine
  // visible if a host renders this before the theme has landed.
  const spine = `var(--rel-${(depth % 4) + 1}, var(--brand))`
  const shown = collection.visibleRelated.rows.length
  const total = related.rows.length
  /* The rows are the OTHER entity's records, so they are counted in its
     registered collection noun ("2 addresses"), never the relationship's label
     ("2 billing address", "3 located at"); the singular at exactly one — the
     registry's own words when it has them (People/Person is not a suffix). */
  const otherIdentity = slot ? identities.data?.find((row) => row.entity_type === slot.otherEntity) : undefined
  const noun = slot ? (otherIdentity?.plural_label?.trim() || slot.otherLabel).toLowerCase() : 'related records'
  const singularNoun = slot ? (otherIdentity?.singular_label?.trim() || singularize(slot.otherLabel)).toLowerCase() : 'related record'
  const countNoun = (n: number) => (n === 1 ? singularNoun : noun)
  const footerContext = slot ? relationFooterContext(slot, relatedOrder.rung.groupOf(slot.id), RELATED_GROUP) : null
  const selectedRecordIds = [...selection]
    .map((rowId) => collection.visibleRelated.rows.find((row, index) => rowKey(row, index) === rowId))
    .filter((row): row is RelationRow => row != null)
    .map((row) => String(row.record.id))

  return (
    <div className={s.band} data-depth={depth} style={{ ['--spine' as string]: spine }}>
      <div className={s.spine} aria-hidden="true" />
      <div className={s.inner}>
        {/* THE NESTED WORKSPACE (#2375 §9): the same CollectionWorkspace one
            depth down — density is the only difference. It hangs from the
            relationship tabs (bracketed groups, counts, drawn direction), and
            its view selector names the relationship; the Marble mark leads its
            one bar exactly as it leads the parent list's — Christopher,
            2026-08-06: "grid bar should always (also for any existing) have
            the marble icon in front which acts like the contextual command K". */}
        <CollectionWorkspace
          identity={`related:${entity}:${slot?.id ?? 'none'}`}
          depth={depth + 1}
          testId={`band-bar-${slot?.id ?? 'none'}`}
          tabs={(
            <RelationshipTabs
              entity={entity} slots={slots} bySlot={bySlot} selected={slot}
              testId={`band-picker-${depth}`}
              onSelect={(next) => { setPickedId(next.id); setOpenRow(null); setSelection(new Set()) }}
            />
          )}
          onCommand={() => openPalette({
            entity: slot?.otherEntity ?? entity,
            label: slot ? `${slot.label} of ${label ?? 'this record'}` : (label ?? 'Related'),
            items: rowItems,
            ...(slot && relationshipWriteOffered(slot, 'create') ? {
              create: {
                label: `New ${slot.otherLabel.toLowerCase()} — linked to ${label ?? 'this record'}`,
                run: () => openQuickCreate({ slot, recordId: id, recordLabel: label }),
              },
            } : {}),
          })}
          commandLabel={slot ? `Command palette for ${slot.label} of ${label ?? 'this record'}` : 'Command palette'}
          /* The relationship's governed New, beside the mark (#2375 §3.3): the
             same create the palette offers, offered only where the slot does. */
          commands={slot && relationshipWriteOffered(slot, 'create') ? (
            <Button variant="primary" size="sm" data-testid={`band-new-${slot.id}`}
              onClick={() => openQuickCreate({ slot, recordId: id, recordLabel: label })}>
              New
            </Button>
          ) : undefined}
          selection={{
            count: selection.size,
            label: `Actions for selected ${noun}`,
            onClear: () => setSelection(new Set()),
            actions: farActions.hasBatch ? farActions.renderBatch(selectedRecordIds) : undefined,
          }}
          search={slot ? <GridSearch value={search} onChange={setSearch} collection={slot.label} /> : undefined}
          /* Was a bare Radix `<Select>`, byte-for-byte the same as
             `RelationCascade`'s (#2406 section 4). At depth > 0 the selector
             carries the RELATIONSHIP's name when it has no view to name. */
          viewSelector={(
            <SavedViewSelector
              views={viewRows.length > 0
                ? viewRows.map((view) => ({ id: String(view.id), name: String(view.name ?? 'View') }))
                : [{ id: 'default', name: slot?.label ?? 'Default view' }]}
              activeId={viewRows.length > 0 ? String(activeView?.id ?? '') : 'default'}
              subject={slot?.label ?? 'relationship'}
              modified={collection.settings.modified}
              onSelect={(next) => { setViewId(next); setSelection(new Set()) }} disabled={viewRows.length === 0} />
          )}
          viewSettings={(
            <ViewSettingsMenu subject={noun} modified={collection.settings.modified}
              onReset={collection.settings.onReset}
              sort={headersHidden ? {
                columns: collection.columnVerbs.columns,
                active: collection.columnVerbs.sortActive,
                onSort: collection.grid.onSortColumn,
              } : undefined}
              group={headersHidden ? {
                columns: collection.columnVerbs.groupable,
                active: collection.columnVerbs.groupActive,
                onGroup: collection.columnVerbs.onGroup,
              } : undefined}
              onColumns={headersHidden ? collection.grid.onChooseColumns : undefined}
              presentation={{
                value: presentation,
                options: [
                  { value: 'grid', label: 'Show as table', icon: <Table2 size={14} aria-hidden /> },
                  { value: 'cards', label: 'Show as cards', icon: <LayoutGrid size={14} aria-hidden /> },
                ],
                onChange: (value) => setPresentation(value as 'grid' | 'cards'),
              }}
              testId={`band-settings-${depth}`} />
          )}
          filters={collection.filters}
          notice={collection.notice}
          footer={slot ? (<>
            {/* The count on the left; on the right the relationship's CONTEXT
                in words (#2375 §9.4): its label, the direction and the group —
                registry labels, never the metamodel's keys (#2406 machine-text). */}
            <span className="tnum" data-testid={`band-count-${depth}`}>
              {shown === total ? `${total} ${countNoun(total)}` : `${shown} of ${total} ${countNoun(total)}`}
            </span>
            <span data-testid={`band-context-${depth}`}>
              {footerContext!.relationship} · {footerContext!.direction}{' '}
              <span aria-hidden="true">{footerContext!.arrow}</span>
              {footerContext!.group ? <> · {footerContext!.group}</> : null}
            </span>
          </>) : undefined}>
          {slot && <BandGrid slot={slot} recordId={id} parentEntity={entity} parentLabel={label}
            depth={depth} path={path} onOpenRecord={onOpenRecord}
            openRow={openRow} setOpenRow={setOpenRow} trail={[...trail, slot.label]}
            related={collection.visibleRelated} countKnown={search || collection.grid.filter ? true : undefined}
            presentation={presentation}
            /* Select + pin are the nested lead's DEFAULT (#2375 §9.4): the
               selection group names what is chosen even where the far entity has
               no batch act to offer; its actions come only from the seam. */
            selection={selection}
            onSelectionChange={setSelection}
            onHeadersHiddenChange={setHeadersHidden}
            {...collection.grid} />}
        </CollectionWorkspace>
        {collection.menus}
      </div>
    </div>
  )
}

/** A band row's stable id — the EDGE for an edge-backed relationship (two edges
 *  may join the same record), else the far record. Shared by the grid and the
 *  band's selection, which maps selected rows back to far-record ids. */
const rowKey = ({ record, edge }: RelationRow, index: number) =>
  edge ? String(edge.id ?? `${record.id}:${index}`) : String(record.id)

/**
 * The child results, AS A GRID.
 *
 * Christopher, 2026-08-06, looking at the band under an organization row: the
 * two people it is related to were bare links. "Check Chris & Hans … no grid."
 *
 * Three things make this the same grid as the one above it rather than a
 * lookalike:
 *
 *  · an FK relation's COLUMNS are the child entity's own default view. An
 *    EDGE relation is different by design: the row is the relationship, so its
 *    columns are far-side label + the type's declared attributes + validity.
 *    The relationship type declaration, never a client-side key list, decides
 *    which edge facts appear;
 *  · the COMPONENT is the shared `DataGrid` used by record-shaped list surfaces,
 *    so its desktop rows, compact cards, keyboard model and disclosure semantics
 *    cannot drift into another private implementation;
 *  · the STRUCTURE remains a real ARIA grid with column headers, so a screen
 *    reader hears column boundaries here too (UX-05). A nested grid is legal
 *    inside the outer grid's cell; the band's row already spans every column.
 *
 * What it deliberately is NOT is `<ListView>`. That is 880 lines of persona bar,
 * view tabs, filter chips, column ⋮ menus, inline editing, selection + batch
 * toolbar, ghost create and a peek aside — and it also registers the AMBIENT
 * palette scope and the drawer context as module-level singletons. Nesting one
 * per open row would mean N of them fighting over both, and the last to mount
 * would win: the ⌘K scope would silently become whichever band rendered last.
 * The band takes ListView's SHAPE and leaves its page machinery on the page.
 */
export function BandGrid({
  slot, recordId, parentEntity, parentLabel, depth, path, openRow, setOpenRow, trail, related,
  peek, peekRecipe,
  countKnown = true, presentation, onOpenRecord,
  columnKeys, sort = null, filter = null, narrowed = false,
  onToggleSort, onSortColumn, onFilterColumn, onHideColumn, onChooseColumns,
  grouping, groupOf, selection, onSelectionChange,
  pinnedRowIds, onPinnedRowIdsChange, pinnedColumns, onTogglePinColumn, widthOf, onResizeColumn, onResetColumn,
  onHeadersHiddenChange,
}: {
  slot: RelationSlot; recordId: string; parentEntity: string; parentLabel?: string
  depth: number; path: string[]
  openRow: string | null; setOpenRow: (id: string | null) => void; trail: string[]
  /** This section reads as PEEK, so a disclosure previews rather than drills. */
  peek?: boolean
  /** The target's declared preview recipe, as the slot resolver handed it over. */
  peekRecipe?: PeekRecipe | null
  related: RelationRows
  /** False means an empty loaded page is not evidence that the slot is empty. */
  countKnown?: boolean
  /** Explicit record-workspace lens; absent preserves the nested band's responsive fallback. */
  presentation?: 'grid' | 'cards'
  columnKeys?: string[]
  /* The order and the filter in force. The bar no longer counts them, so the
     HEADERS carry them: ↑/↓ and `aria-sort` on the sorted column, the ▽ mark and
     its summary on the filtered one. */
  sort?: RelationSortTerm | null
  filter?: RelationFilterTerm | null
  /** A search or filter is in force, so no rows means none MATCH. */
  narrowed?: boolean
  /** A header click: ascending, then descending, then the view's own order. */
  onToggleSort?: (key: string) => void
  onSortColumn?: (key: string, direction: 'asc' | 'desc') => void
  /** Opens the value field AT the column, rather than only choosing it. */
  onFilterColumn?: (key: string, anchor: HTMLElement) => void
  onHideColumn?: (key: string) => void
  /** "Columns…" in every column's menu — the one way back for a hidden column. */
  onChooseColumns?: (anchor: HTMLElement) => void
  /** Grouped rows over a COMPLETE read (ADR F-84 §2); the collection decides. */
  grouping?: GridGrouping
  /** "Group by" for a column, where this read can group it exactly. */
  groupOf?: (key: string) => { active: boolean; onToggle: () => void } | undefined
  /** The nested lead's select slot (#2375 §9.4: select + pin by default). */
  selection?: Set<string>
  onSelectionChange?: (next: Set<string>) => void
  /** Row pins, this tab's own (#2375 §9.4). */
  pinnedRowIds?: string[]
  onPinnedRowIdsChange?: (ids: string[]) => void
  /** "Pin column left" and "Autosize" (#2375 §6.3), this tab's own layout. */
  pinnedColumns?: readonly string[]
  onTogglePinColumn?: (key: string) => void
  widthOf?: (key: string) => number | undefined
  onResizeColumn?: (key: string, px: number) => void
  onResetColumn?: (key: string) => void
  /** The header went inert under a cards reflow — the host moves its verbs (#2375). */
  onHeadersHiddenChange?: (hidden: boolean) => void
  onOpenRecord?: (entity: string, id: string, name?: string) => void
}) {
  const schema = useSchema()
  const updateEdge = useUpdateRelationshipEdge(slot, recordId)
  const endEdge = useEndRelationshipEdge(slot, recordId)
  const [editingEdge, setEditingEdge] = useState<string | null>(null)
  const [edgeOperation, setEdgeOperation] = useState<'update' | 'end'>('update')
  const [edgeDraft, setEdgeDraft] = useState<Record<string, unknown>>({})
  const { rows, isLoading, isError, error } = related
  const recordRefs = useMemo(
    () => rows.map(({ record }) => ({ type: slot.otherEntity, id: String(record.id) })),
    [rows, slot.otherEntity],
  )
  const identities = useRecordIdentities(recordRefs)
  // Four, not six: the band is indented inside a row of the list above it, and a
  // six-column child grid inside a six-column parent is where the reader loses
  // which grid they are reading.
  const { columns, fields } = useDefaultColumns(slot.otherEntity, 4)
  const isEdge = slot.storage === 'edge'
  // Can the OTHER end be drilled at all? An entity with no registered list slots
  // has nothing to open, and a twist that opens an empty band is a lie.
  const childDrillable = useMemo(
    () => listSlots(schema.data, slot.otherEntity).length > 0,
    [schema.data, slot.otherEntity])

  // A refused read is not an empty list (#389) — say so rather than inviting a
  // duplicate of a record the reader is not allowed to see.
  if (isError) return <p className={s.err} role="alert">{(error as Error)?.message || 'That could not be read.'}</p>
  if (isLoading) return <div className={s.loading}><Spinner /></div>
  if (rows.length === 0) {
    /* NARROWED TO NOTHING is not EMPTY. Filtering a billing-address grid to a
       value no row held printed "No addresses yet. Create one" above a footer
       that said "0 of 2" — inviting a duplicate of records that were there.
       The chip above the grid is the way back out. */
    if (narrowed) {
      return <p className={s.empty} role="status"
        data-testid={`band-no-match-${slot.id}`}>No {slot.otherLabel.toLowerCase()} match.</p>
    }
    if (!countKnown) {
      return <p className={s.empty} role="status"
        data-testid={`band-count-unknown-${slot.id}`}>Connection count unavailable.</p>
    }
    return (
      <p className={s.empty}>
        No {slot.otherLabel.toLowerCase()} yet.
        {relationshipWriteOffered(slot, 'create') && (
          <>{' '}<button className={s.inline} data-testid={`band-empty-new-${slot.id}`}
            onClick={() => openQuickCreate({ slot, recordId })}>Create one</button></>
        )}
      </p>
    )
  }

  /* Column 1 is the twist + the record's name; the view's own columns follow.
     The name column is not one of them: the reader needs to know WHICH record a
     row is before they need any of its fields, and a default view whose first
     column happens to be `status` would otherwise open a nameless grid. */
  const canExpand = ({ record }: RelationRow) =>
    identities.canNavigate(slot.otherEntity)
    && childDrillable && depth < MAX_DEPTH && !path.includes(`${slot.otherEntity}:${String(record.id)}`)

  type BandDataRow = GridRow & {
    relation: RelationRow
    rid: string
    recordLabel: string
    onPath: boolean
    canOpen: boolean
    attributeValues: string[]
    validity: ReturnType<typeof edgeValidity>
    accessibleFacts: string[]
    identityIconKey: string
  }
  const asBandRow = (row: GridRow) => row as BandDataRow
  const gridRows: BandDataRow[] = rows.map((relation, index) => {
    const { record, edge } = relation
    const rid = String(record.id)
    const identity = identities.identity(slot.otherEntity, rid)
    const recordLabel = identity.resolved ? identity.title : displayLabel(record)
    const edgeAttributes = edge?.attributes && typeof edge.attributes === 'object'
      ? edge.attributes as Record<string, unknown>
      : {}
    const attributeValues = slot.attributes.map((attribute) => edgeAttributeValue(attribute, edgeAttributes[attribute.key]))
    const validity = edgeValidity(edge)
    const accessibleFacts = isEdge
      ? [...attributeValues.filter((value) => value !== '—'), validity.label].filter(Boolean)
      : []
    return {
      id: rowKey(relation, index), relation, rid, recordLabel,
      onPath: path.includes(`${slot.otherEntity}:${rid}`), canOpen: canExpand(relation),
      attributeValues, validity, accessibleFacts, identityIconKey: identity.iconKey,
    }
  })
  /* A plain read-only relationship grid has no roving row entry point. Its
     record links therefore remain ordinary tab stops: that both makes every
     destination reachable and gives an overflowing grid a keyboard-focusable
     descendant. When any row can expand, DataGrid owns one roving row stop and
     ArrowRight enters the record link; keeping every link in the document tab
     order as well would add one stop per row and defeat the grid contract. */
  const rowsHaveDisclosure = gridRows.some((row) => row.canOpen)

  const nameColumn: GridColumn = {
    key: RECORD_COLUMN, label: slot.otherLabel, width: 'minmax(180px, 1.6fr)', compact: 'primary',
    cell: (row) => {
      const data = asBandRow(row)
      return (
        <>
          {!identities.canNavigate(slot.otherEntity) ? (
            <span className={s.rowLink} data-non-record-surface="">
              <EntityIcon iconKey={data.identityIconKey} size={15} />{data.recordLabel}
            </span>
          ) : onOpenRecord ? (
            /* A record's name is a LINK to the record, in both branches. The host
               that opens related records its own way (a peek, a desk) still
               gets the plain click; the anchor keeps the canonical address for
               a new tab and for the reader, where a <button> drawn to look like
               a link had neither (#2406 rogue census, app-styled-control). */
            <Link to="/e/$entity/$id" params={{ entity: slot.otherEntity, id: data.rid }} className={s.rowLink}
              data-record-link="" tabIndex={rowsHaveDisclosure ? -1 : undefined}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                event.preventDefault()
                onOpenRecord(slot.otherEntity, data.rid, data.recordLabel)
              }}>
              <EntityIcon iconKey={data.identityIconKey} size={15} />{data.recordLabel}
            </Link>
          ) : (
            <Link to="/e/$entity/$id" params={{ entity: slot.otherEntity, id: data.rid }} className={s.rowLink}
              data-record-link="" tabIndex={rowsHaveDisclosure ? -1 : undefined}
              onClick={() => extendTrail(
                window.location.pathname,
                { label: parentLabel ?? 'Record', to: `/e/${parentEntity}/${recordId}` },
                `/e/${slot.otherEntity}/${data.rid}`)}>
              <EntityIcon iconKey={data.identityIconKey} size={15} />{data.recordLabel}
            </Link>
          )}
          {data.onPath && <span className={s.cycle} title="Already open further up this path">↑ on this path</span>}
          {!data.canOpen && !data.onPath && depth >= MAX_DEPTH && (
            <span className={s.deep} title="Open the record to keep going">deepest here</span>
          )}
        </>
      )
    },
  }
  const shows = (key: string) => columnKeys == null || columnKeys.includes(key)
  /* THE EDGE'S OWN ACT IS A COLUMN, NOT A WORD SQUEEZED BESIDE THE DATE
     (#2406 rogue census). It was an underlined "Edit" inside the Validity cell,
     drawn by a private class: an app-styled control, and on a phone the cell's
     90px held "Current" and cut "Edit" at its clip (control-clipped). It is the
     kit's icon Button now, in a trailing column sized for the control at the
     pointer's floor, and named in words for the reader. Not offered by the
     slot → no column at all, rather than an empty one. It is not a value, so
     it neither sorts nor joins the column chooser, and a saved view's column
     list cannot hide the only way to edit the relationship. */
  const edgeUpdate = relationshipWriteOffered(slot, 'update')
  const edgeWritable = isEdge && (edgeUpdate || relationshipWriteOffered(slot, 'end'))
  const edgeActColumn: GridColumn = {
    key: '__edgeAct', label: edgeUpdate ? 'Edit' : 'End', align: 'right', sortable: false, menuable: false,
    width: 'calc(max(var(--ctl-h), var(--ctl-h-floor)) + var(--sp-6))', compact: 'actions',
    cell: (row) => {
      const data = asBandRow(row)
      const edge = data.relation.edge
      if (edge?.id == null) return null
      const name = `${edgeUpdate ? 'Edit relationship facts' : 'End relationship'} for ${data.recordLabel}`
      return (
        <Button variant="ghost" size="sm" className={s.edgeAct} aria-label={name} title={name}
          icon={edgeUpdate ? <Pencil size={14} aria-hidden /> : <Unlink size={14} aria-hidden />}
          onClick={(event) => {
            event.stopPropagation()
            setOpenRow(null)
            setEditingEdge(String(data.id))
            setEdgeOperation(edgeUpdate ? 'update' : 'end')
            const attributes = edge.attributes && typeof edge.attributes === 'object'
              ? edge.attributes as Record<string, unknown>
              : {}
            setEdgeDraft({ ...attributes, valid_from: edge.valid_from ?? '', valid_to: edge.valid_to ?? new Date().toISOString().slice(0, 10) })
          }} />
      )
    },
  }
  const dataColumns: GridColumn[] = isEdge
    ? ([
        ...slot.attributes.map((attribute, columnIndex): GridColumn => ({
          key: attribute.key, label: attribute.label, width: 'minmax(90px, 1fr)', compact: 'detail',
          cell: (row) => {
            const value = asBandRow(row).attributeValues[columnIndex]!
            return value === '—' ? <span className={s.blank}>—</span> : value
          },
        })),
        {
          key: '__validity', label: 'Validity', width: 'minmax(90px, 1fr)', compact: 'detail',
          cell: (row) => {
            const data = asBandRow(row)
            return <span className={data.validity.ended ? s.ended : undefined}>{data.validity.label}</span>
          },
        },
      ] satisfies GridColumn[]).filter((column) => shows(column.key))
        .concat(edgeWritable ? [edgeActColumn] : [])
    : (columnKeys ?? columns).map((column): GridColumn => ({
        key: column, label: String(fields.get(column)?.label ?? column), width: 'minmax(90px, 1fr)', compact: 'detail',
        cell: (row) => {
          const value = asBandRow(row).relation.record[column]
          return value == null || value === '' ? <span className={s.blank}>—</span> : String(value)
        },
      }))
  const expandedRow = editingEdge ?? openRow
  const decorate = (column: GridColumn): GridColumn => ({
    ...column,
    sort: sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none',
    filtered: filter?.column === column.key || undefined,
    filterSummary: filter?.column === column.key ? `${column.label} contains “${filter.value}”` : undefined,
  })

  return (
    <div className={s.grid} data-testid={`band-rows-${slot.id}`}>
      <DataGrid columns={[nameColumn, ...dataColumns].map(decorate)} rows={gridRows}
        onSortColumn={onToggleSort ? (key) => onToggleSort(key) : undefined}
        compact={presentation ? undefined : 'cards'} presentation={presentation}
        grouping={grouping}
        selection={selection} onSelectionChange={onSelectionChange}
        pinnedRowIds={pinnedRowIds} onPinnedRowIdsChange={onPinnedRowIdsChange}
        pinnedColumns={pinnedColumns}
        widthOf={widthOf} onResizeColumn={onResizeColumn} onResetColumn={onResetColumn}
        onHeadersHiddenChange={onHeadersHiddenChange}
        rowName={(row) => {
          const data = asBandRow(row)
          return [data.recordLabel, ...data.accessibleFacts].join(', ')
        }}
        itemsLabel={slot.otherLabel.toLowerCase()}
        renderExpansion={(row) => {
          const data = asBandRow(row)
          const edge = data.relation.edge
          if (editingEdge === data.id && edge?.id != null) {
            const write = edgeOperation === 'update' ? updateEdge : endEdge
            return (
              <>
                {write.isError && <p role="alert">{(write.error as Error)?.message || 'That relationship could not be saved.'}</p>}
                <Form fields={edgeOperation === 'update'
                  ? [...relationshipAttributeFields(slot), ...relationshipLifecycleFields()]
                  : [relationshipLifecycleFields()[1]!]} value={edgeDraft}
                  onChange={(patch) => setEdgeDraft((current) => ({ ...current, ...patch }))}
                  onSubmit={() => {
                    const attributes = Object.fromEntries(slot.attributes.map(({ key }) => [key, edgeDraft[key]]))
                    const pending = edgeOperation === 'update'
                      ? updateEdge.mutateAsync({
                        id: String(edge.id), attributes, otherId: data.rid,
                        valid_from: edgeDraft.valid_from, valid_to: edgeDraft.valid_to,
                      })
                      : endEdge.mutateAsync({ id: String(edge.id), otherId: data.rid, on: String(edgeDraft.valid_to ?? '') })
                    void pending.then(() => setEditingEdge(null)).catch(() => undefined)
                  }}
                  onCancel={() => setEditingEdge(null)} submitLabel={edgeOperation === 'update' ? 'Save relationship facts' : 'End relationship'}
                  busy={updateEdge.isPending || endEdge.isPending} variant="quick" layout="grid"
                  testid={`edge-form-${String(edge.id)}`} />
              </>
            )
          }
          /* PEEK expands into the record's own canonical form (#2281); every
             other lens drills into the next relationship band. Same disclosure,
             same row, same `aria-expanded`/`aria-controls` — what changes is
             what the disclosure contains, which is the whole of the lens. */
          if (peek) {
            return <PeekRow entity={slot.otherEntity} id={data.rid} recipe={peekRecipe ?? null}
              testId={`peek-${data.rid}`}
              onOpenRecord={onOpenRecord && data.canOpen
                ? () => onOpenRecord(slot.otherEntity, data.rid, data.recordLabel)
                : undefined} />
          }
          return <RelationBand entity={slot.otherEntity} id={data.rid} label={data.recordLabel}
            depth={depth + 1} visited={path} trail={trail} onOpenRecord={onOpenRecord} />
        }}
        canExpandRow={(row) => asBandRow(row).canOpen}
        expandedRowId={expandedRow}
        onExpandedRowIdChange={(next) => { setEditingEdge(null); setOpenRow(next) }}
        expandTestId={(row) => `band-twist-${asBandRow(row).rid}`}
        expandLabel={(row, expanded) => `${expanded ? 'Collapse' : 'Expand'} ${asBandRow(row).recordLabel}`}
        decorateRow={(row) => asBandRow(row).validity.ended ? { tone: 'muted' } : undefined}
        /* The kit's column ⋮ menu (#2375 §6.3) — the same one the root list
           and the portal use. The column's OWN label heads it: an edge
           attribute is not one of the far entity's fields, so a field lookup
           printed its raw key here. */
        renderColumnMenu={({ column, anchor, close, autosize }) => (
          <GridColumnMenu anchor={anchor} onClose={close} columnKey={column}
            label={[nameColumn, ...dataColumns].find((c) => c.key === column)?.label ?? column}
            sort={onSortColumn ? {
              direction: sort?.key === column ? sort.direction : null,
              onSort: (direction) => onSortColumn(column, direction),
            } : undefined}
            onFilter={onFilterColumn ? () => onFilterColumn(column, anchor) : undefined}
            group={groupOf?.(column)}
            pin={onTogglePinColumn
              ? { pinned: pinnedColumns?.includes(column) ?? false, onToggle: () => onTogglePinColumn(column) }
              : undefined}
            onAutosize={autosize}
            onHide={onHideColumn && column !== RECORD_COLUMN && dataColumns.length > 1
              ? () => onHideColumn(column) : undefined}
            onColumns={onChooseColumns ? () => onChooseColumns(anchor) : undefined} />
        )} />
    </div>
  )
}

/** Render one declared edge fact without inventing a schema in the client. */
export function edgeAttributeValue(
  attribute: RelationSlot['attributes'][number], value: unknown,
): string {
  if (value == null || value === '') return '—'
  const option = attribute.options.find((o) => String(o.value) === String(value))
  if (option) return option.label
  if (attribute.kind === 'boolean' && typeof value === 'boolean') return value ? 'Yes' : 'No'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** A temporal edge must read as past wherever it appears. */
export function edgeValidity(edge: Row | null): { label: string; ended: boolean } {
  if (!edge) return { label: '', ended: false }
  const from = edge.valid_from == null ? '' : String(edge.valid_from)
  const to = edge.valid_to == null ? '' : String(edge.valid_to)
  if (to) return { label: from ? `${from} – ${to} · Ended` : `Ended ${to}`, ended: true }
  return { label: from ? `Since ${from}` : 'Current', ended: false }
}
